import { Form, Formik } from 'formik'
import { FC, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { ID } from '@models/base'
import { Option } from '@models/option'
import { QueueModel } from '@models/queue'
import { QueueNodeCompleteActionEnum } from '@models/queueProcess'

import { Button } from '@components/Button'
import { FormControl } from '@components/FormControl'

import { PermissionsControl } from '@modules/permissions'
import QueueSelectField from '@modules/queues/components/QueueSelectField/QueueSelectField'

import { PermissionEnum } from '@/constants/permission'
import AxiosUtil from '@/utils/axiosUtil'
import FormUtil from '@/utils/formUtil'

import { CompleteQueueTypeRadioButton } from '../QueueNumberCompleteFormModal/fields/CompleteQueueTypeRadioButton'
import { QueueCompleteCountersSelectField } from '../QueueNumberCompleteFormModal/fields/QueueCompleteCountersSelectField'

export type QueueNumberCompleteFormShape = {
  queue_number_id?: ID
  next_queue_id?: Option<QueueModel>
  preferred_counter_id?: Option
  is_next_queue_allow_multiple_process?: boolean
}

export type Props = {
  queueId?: ID
  isEnding?: boolean
  initialValues?: QueueNumberCompleteFormShape
  onHide: () => void
  onSubmit: (values: QueueNumberCompleteFormShape) => Promise<void>
}

const QueueNumberCompleteForm: FC<Props> = ({ queueId, isEnding, initialValues, onHide, onSubmit }) => {
  const [error, setError] = useState<string>()
  const [action, setAction] = useState<QueueNodeCompleteActionEnum>(QueueNodeCompleteActionEnum.TRANSFER)

  return (
    <Formik<QueueNumberCompleteFormShape>
      initialValues={{ ...initialValues }}
      onSubmit={async (values, { setSubmitting, setErrors }) => {
        try {
          setSubmitting(true)

          if (action === QueueNodeCompleteActionEnum.END) {
            values.next_queue_id = undefined
          }

          await onSubmit(values)
        } catch (err) {
          if (AxiosUtil.isAxiosError(err)) {
            const errors = FormUtil.parseErrorResponse(err.response?.data.errors)
            if (errors) {
              setErrors(errors)
            } else {
              setError(err.response?.data.message)
            }
            console.error(err)
          }
        } finally {
          setSubmitting(false)
        }
      }}
    >
      {({ handleSubmit, isSubmitting, values, setFieldValue, errors }) => (
        <Form
          className="form"
          onSubmit={handleSubmit}
          noValidate
        >
          {/* begin::Alert */}
          {error && <div className="alert alert-danger d-flex align-items-center p-5 mb-10">{error}</div>}
          {/* end::Alert */}

          {/* begin::Scroll */}
          <CompleteQueueTypeRadioButton
            value={action}
            isEnding={isEnding}
            onChange={(nextAction) => setAction(nextAction)}
          />

          {action === QueueNodeCompleteActionEnum.TRANSFER && (
            <>
              <PermissionsControl
                allow={PermissionEnum.QUEUE_OPTION}
                renderError
              >
                <FormControl>
                  <FormControl.Label isRequired>
                    <FormattedMessage id="queue_node.complete.move_to" />
                  </FormControl.Label>

                  <QueueSelectField
                    name="next_queue_id"
                    exceptQueueId={queueId}
                    onChange={(value) => {
                      if (value) {
                        const option = value as Option<QueueModel>
                        setFieldValue('is_next_queue_allow_multiple_process', option.data?.allow_multiple_process)
                      } else {
                        setFieldValue('is_next_queue_allow_multiple_process', undefined)
                      }
                    }}
                  />
                  <FormControl.ErrorMessage message={errors.next_queue_id} />
                </FormControl>
              </PermissionsControl>

              {values.next_queue_id && !values.is_next_queue_allow_multiple_process && (
                <PermissionsControl
                  allow={
                    // eslint-disable-next-line max-len
                    PermissionEnum.QUEUE_COUNTER_OPTION_FOR_QUEUE_NUMBER_COMPLETE_FORM
                  }
                  renderError
                >
                  <FormControl>
                    <FormControl.Label>
                      <FormattedMessage id="choose.preferred.counter" />
                    </FormControl.Label>
                    <QueueCompleteCountersSelectField
                      isClearable
                      name="preferred_counter_id"
                      queueId={values.next_queue_id.value as ID}
                    />
                    <FormControl.ErrorMessage message={errors.preferred_counter_id} />
                  </FormControl>
                </PermissionsControl>
              )}
            </>
          )}
          {/* end::Scroll */}

          {/* begin::Actions */}
          <div className="d-flex pt-15 justify-content-center gap-3">
            <Button
              theme="light"
              disabled={isSubmitting}
              onClick={onHide}
            >
              <FormattedMessage id="form.cancel" />
            </Button>

            <Button
              type="submit"
              theme="primary"
              isLoading={isSubmitting}
            >
              {action === QueueNodeCompleteActionEnum.TRANSFER ? (
                <FormattedMessage id="queue_node.complete.button_move_to" />
              ) : (
                <FormattedMessage id="queue_node.complete.button_end" />
              )}
            </Button>
          </div>
          {/* end::Actions */}
        </Form>
      )}
    </Formik>
  )
}

export { QueueNumberCompleteForm }
